// ============================================================
// Isca do Jhow: a conversa (fase 1, SDD-jhow-isca-fase1)
// ============================================================
// Antes do login nada e gravado (ADR-036): a conversa mora no navegador e volta
// assinada a cada mensagem. Aqui ficam a forma dela, os limites e a leitura das
// marcas que o modelo emite. Nada aqui toca banco nem rede, e o arquivo roda no
// navegador tambem (a tela usa a abertura e os tipos).
// ============================================================

/** A primeira fala do Jhow, fixa na pagina: sem custo de IA. */
export const ABERTURA_ISCA = 'Me conta o que você faz, do jeito que contaria para um amigo.'

/** O que entra como fala da pessoa quando ela confirma a proposta no botao. */
export const CONFIRMACAO_ISCA = 'É isso.'

export type Papel = 'jhow' | 'pessoa'
export type FaseIsca = 'montando' | 'explicando' | 'convite'

export interface MensagemIsca {
  papel: Papel
  texto: string
}

export interface ConversaIsca {
  v: 1
  iniciada_em: string
  fase: FaseIsca
  /** A resposta que a pessoa confirmou (no botao). Nula ate a confirmacao. */
  resposta: string | null
  /** A ultima versao que o Jhow propos e a pessoa ainda nao confirmou. */
  proposta: string | null
  /** Quantas vezes o Jhow ja falou em cada momento (a abertura fixa nao conta). */
  falas: { montando: number; explicando: number }
  mensagens: MensagemIsca[]
}

export interface ConversaAssinada extends ConversaIsca {
  assinatura: string
}

export const LIMITES = {
  caracteresPorMensagem: 1000,
  /** A missao pede no maximo 6 perguntas; a partir da sexta o Jhow e instruido a fechar. */
  perguntasMontando: 6,
  /** 6 perguntas, a proposta corrigida uma vez e a fala que confirma: 8. */
  falasMontando: 8,
  /** Explicar e convidar, somando o que vier depois do convite. */
  falasExplicando: 4,
  validadeContinuarMs: 24 * 60 * 60 * 1000,
  validadeSementeMs: 7 * 24 * 60 * 60 * 1000,
  /** Teto de tamanho de qualquer fala guardada (a do Jhow inclusive). */
  caracteresPorFala: 4000,
} as const

const FASES: ReadonlySet<string> = new Set(['montando', 'explicando', 'convite'])
const MAX_MENSAGENS = 1 + 2 * (LIMITES.falasMontando + LIMITES.falasExplicando)

export function novaConversa(agora: Date): ConversaIsca {
  return {
    v: 1,
    iniciada_em: agora.toISOString(),
    fase: 'montando',
    resposta: null,
    proposta: null,
    falas: { montando: 0, explicando: 0 },
    mensagens: [{ papel: 'jhow', texto: ABERTURA_ISCA }],
  }
}

/**
 * Confere a forma do que veio do navegador. Nao confere a assinatura: isso e da
 * assinatura.ts. Papeis alternados, comecando pela abertura fixa do Jhow, e
 * terminando no Jhow (e assim que o servidor devolve).
 */
export function formaValida(x: unknown): x is ConversaAssinada {
  if (!x || typeof x !== 'object') return false
  const c = x as Record<string, unknown>
  if (c.v !== 1) return false
  if (typeof c.iniciada_em !== 'string' || Number.isNaN(Date.parse(c.iniciada_em))) return false
  if (typeof c.fase !== 'string' || !FASES.has(c.fase)) return false
  if (c.resposta !== null && (typeof c.resposta !== 'string' || c.resposta.length > LIMITES.caracteresPorFala)) return false
  if (c.proposta !== null && (typeof c.proposta !== 'string' || c.proposta.length > LIMITES.caracteresPorFala)) return false
  if (typeof c.assinatura !== 'string' || c.assinatura.length === 0) return false
  const f = c.falas as Record<string, unknown> | undefined
  if (!f || !Number.isInteger(f.montando) || !Number.isInteger(f.explicando)) return false
  if ((f.montando as number) < 0 || (f.explicando as number) < 0) return false
  if (!Array.isArray(c.mensagens) || c.mensagens.length < 1 || c.mensagens.length > MAX_MENSAGENS) return false
  for (let i = 0; i < c.mensagens.length; i++) {
    const m = c.mensagens[i] as Record<string, unknown>
    if (!m || typeof m.texto !== 'string' || m.texto.length > LIMITES.caracteresPorFala) return false
    const esperado: Papel = i % 2 === 0 ? 'jhow' : 'pessoa'
    if (m.papel !== esperado) return false
  }
  const primeira = c.mensagens[0] as MensagemIsca
  if (primeira.texto !== ABERTURA_ISCA) return false
  return (c.mensagens.length - 1) % 2 === 0
}

/** Texto da pessoa: aparado, sem vazio, dentro do limite. */
export function textoDaPessoa(x: unknown): string | null {
  if (typeof x !== 'string') return null
  const t = x.trim()
  if (!t || t.length > LIMITES.caracteresPorMensagem) return null
  return t
}

/** Ainda cabe uma fala do Jhow no momento atual? */
export function cabeMaisUmaFala(c: ConversaIsca): boolean {
  if (c.fase === 'montando') return c.falas.montando < LIMITES.falasMontando
  return c.falas.explicando < LIMITES.falasExplicando
}

export interface MarcasLidas {
  /** O texto para a tela: sem as marcas (a proposta fica no lugar) e sem formatacao. */
  limpo: string
  /** A versao proposta pelo Jhow, a pessoa ainda vai confirmar. */
  proposta: string | null
  convite: boolean
}

/**
 * Travessao, negrito, titulo e linha de separacao saem no codigo: a instrucao
 * pede, e o modelo escreve assim mesmo (bateria de 27/09). O chat mostra texto
 * puro, entao os asteriscos apareceriam crus.
 */
export function limparFormato(t: string): string {
  return t
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s.,;:!?)]|$)/gm, '$1$2')
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, '')
    .replace(/^[ \t]*(-{3,}|\*{3,}|_{3,})[ \t]*$/gm, '')
    .replace(/[ \t]*[\u2014\u2013][ \t]*/g, ', ')
    .replace(/,[ \t]*,/g, ',')
    .replace(/,[ \t]*([.!?])/g, '$1')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** Le <proposta>...</proposta> (e o antigo <resposta>, como proposta) e [CONVITE] na fala do modelo. */
export function lerMarcas(texto: string): MarcasLidas {
  let proposta: string | null = null
  const m = texto.match(/<(proposta|resposta)>([\s\S]*?)<\/(proposta|resposta)>/i)
  if (m) proposta = limparFormato(m[2]) || null
  const convite = /\[CONVITE\]/i.test(texto)
  const limpo = limparFormato(
    texto
      .replace(/<\/?(proposta|resposta)>/gi, '')
      .replace(/\[CONVITE\]/gi, '')
      .replace(/\[RESPOSTA_PRONTA\]/gi, ''),
  )
  return { limpo, proposta, convite }
}

/** Todas as falas da pessoa, na ordem. */
export function falasDaPessoa(c: ConversaIsca): string[] {
  return c.mensagens.filter((m) => m.papel === 'pessoa').map((m) => m.texto)
}

/** A conversa em texto corrido, para o pedido ao modelo e para a abertura da etapa 1. */
export function serializar(c: ConversaIsca): string {
  return c.mensagens.map((m) => `${m.papel === 'jhow' ? 'Jhow' : 'Pessoa'}: ${m.texto}`).join('\n\n')
}
